import { useFormik } from "formik";
import * as Yup from "yup";
import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import {
  getUsersForCierreTurnoService,
  closeTurnoService,
  getUserTurnsService,
  downloadAndSavePDF,
} from "modules/came/services/userCameService";
import { generarPDFCierreTurno } from "../components/Form/GeneradorPDFCierre";
import { historialPermitidoPorNombre } from "../historyAccess";

export const useFormCierreTurno = (onSubmit = () => {}, initialData = {}) => {
  const isMounted = useRef(true);
  const hasLoadedUsersRef = useRef(false);

  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [errorUsers, setErrorUsers] = useState(null);
  const [userNotInList, setUserNotInList] = useState(false);
  const [userNotFound, setUserNotFound] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [backendCierreResult, setBackendCierreResult] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);

  const [autoProcessing, setAutoProcessing] = useState(false);
  const [autoProcessingError, setAutoProcessingError] = useState(null);
  const [pdfGeneratedData, setPdfGeneratedData] = useState(null);

  const [userTurns, setUserTurns] = useState([]);
  const [loadingTurns, setLoadingTurns] = useState(false);
  const [selectedTurn, setSelectedTurn] = useState(null);
  const [errorTurns, setErrorTurns] = useState(null);

  const formatDateForAPI = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.000`;
  };

  const parseInputToNumber = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === "number") return value;
    if (typeof value === "string") {
      if (value.trim() === "") return 0;
      let cleaned = value
        .replace(/\./g, "")
        .replace(",", ".")
        .replace(/[^0-9.-]/g, "");
      const num = parseFloat(cleaned);
      return isNaN(num) ? 0 : num;
    }
    return 0;
  };

  const formatNumberForDisplay = (value) => {
    if (value === null || value === undefined) return "";
    if (value === "") return "";
    const numValue = parseInputToNumber(value);
    return new Intl.NumberFormat("es-CO", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numValue);
  };

  const analyzeLocalStorageData = useCallback(() => {
    try {
      const userData = localStorage.getItem("user");
      if (!userData) return { user: null, role: null, rawData: null };
      const parsed = JSON.parse(userData);
      let extractedUser = {
        id: null,
        username: null,
        email: null,
        role: null,
        rawData: parsed,
      };
      if (parsed.user?.username) {
        extractedUser.username = parsed.user.username;
        extractedUser.id = parsed.user.id;
        extractedUser.email = parsed.user.email;
        extractedUser.role = parsed.user.role;
      } else if (parsed.username) {
        extractedUser.username = parsed.username;
        extractedUser.id = parsed.id;
        extractedUser.email = parsed.email;
        extractedUser.role = parsed.role;
      } else if (parsed.more?.username) {
        extractedUser.username = parsed.more.username;
        extractedUser.id = parsed.more.id;
        extractedUser.email = parsed.more.email;
        extractedUser.role = parsed.more.role;
      }
      if (!extractedUser.username || !extractedUser.role) {
        const searchRecursively = (obj) => {
          if (!obj || typeof obj !== "object") return;
          Object.keys(obj).forEach((key) => {
            const value = obj[key];
            if (key.toLowerCase() === "username" && !extractedUser.username) {
              extractedUser.username = value;
            }
            if (key.toLowerCase() === "role" && !extractedUser.role) {
              extractedUser.role = value;
            }
            if (key.toLowerCase() === "id" && !extractedUser.id) {
              extractedUser.id = value;
            }
            if (typeof value === "object" && value !== null) {
              searchRecursively(value);
            }
          });
        };
        searchRecursively(parsed);
      }
      return {
        user: extractedUser,
        role: extractedUser.role,
        rawData: parsed,
      };
    } catch (error) {
      console.error("❌ [useFormCierreTurno] Error:", error);
      return { user: null, role: null, rawData: null };
    }
  }, []);

  useEffect(() => {
    const { user, role } = analyzeLocalStorageData();
    if (user) {
      setCurrentUser(user);
      setUserRole(role);
    }
    setLoading(false);
    return () => {
      isMounted.current = false;
    };
  }, [analyzeLocalStorageData]);

  const getCurrentUsername = useCallback(() => {
    return currentUser?.username || "Usuario no identificado";
  }, [currentUser]);

  const { isAdmin, isUsuarioIserra } = useMemo(() => {
    let roleName = "";
    let roleId = null;
    let roleStr = "";
    if (userRole && typeof userRole === "object" && userRole !== null) {
      roleId = userRole.id;
      roleName = userRole.name || userRole.nombre || userRole.roleName || "";
      roleStr = roleName.toString().toLowerCase();
      if (roleId === 10 || roleId === 14 || roleId === 15) {
        return { isAdmin: true, isUsuarioIserra: false };
      }
    } else if (userRole) {
      roleStr = userRole.toString().toLowerCase();
    }
    const username = getCurrentUsername().toLowerCase();
    const isAdminRole =
      historialPermitidoPorNombre(roleStr) ||
      roleStr.includes("admin iserra100") ||
      roleStr.includes("administrador") ||
      username === "admin" ||
      roleStr.includes("admin") ||
      roleStr.includes("gerencia") ||
      roleStr.includes("supervisor") ||
      (userRole &&
        typeof userRole === "object" &&
        (userRole.id === 10 || userRole.id === 14 || userRole.id === 15));
    const isUsuarioIserraRole =
      roleStr.includes("usuario iserra") ||
      roleStr.includes("user iserra") ||
      roleStr.includes("iserra");
    return {
      isAdmin: isAdminRole,
      isUsuarioIserra: isUsuarioIserraRole,
    };
  }, [getCurrentUsername, userRole]);

  const isOperador = useMemo(() => !isAdmin && !isUsuarioIserra, [isAdmin, isUsuarioIserra]);

  const loadUsers = useCallback(async () => {
    if (hasLoadedUsersRef.current || loadingUsers || !currentUser) return;
    try {
      setLoadingUsers(true);
      setErrorUsers(null);
      setUserNotInList(false);
      setUserNotFound(false);
      const response = await getUsersForCierreTurnoService();
      if (!isMounted.current) return;
      let usersArray = [];
      if (response?.success) {
        if (Array.isArray(response.data)) {
          usersArray = response.data;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          usersArray = response.data.data;
        } else if (response.data && typeof response.data === "object") {
          const possibleFields = ["usuarios", "users", "userList", "data"];
          for (const field of possibleFields) {
            if (Array.isArray(response.data[field])) {
              usersArray = response.data[field];
              break;
            }
          }
        }
      } else if (Array.isArray(response)) {
        usersArray = response;
      }
      const mappedUsers = usersArray
        .filter((user) => {
          const usernameField =
            user.Usuario ||
            user.usuario ||
            user.username ||
            user.userName ||
            user.nombre ||
            user.name;
          return usernameField && typeof usernameField === "string" && usernameField.trim() !== "";
        })
        .map((user, index) => ({
          id: user.id || user.ID || user.Id || user.userId || index + 1,
          username:
            user.Usuario ||
            user.usuario ||
            user.username ||
            user.userName ||
            user.nombre ||
            user.name ||
            `user-${index}`,
          email: user.email || user.Email || "",
          rawUser: user,
        }));
      const currentUsernameLower = getCurrentUsername().toLowerCase();
      const userExistsInList = mappedUsers.some(
        (user) => user.username.toLowerCase() === currentUsernameLower
      );
      let finalUsers = [];
      let shouldShowError = false;
      let errorMessage = "";
      if (isAdmin) {
        finalUsers = mappedUsers;
      } else {
        if (userExistsInList) {
          const matchingUser = mappedUsers.find(
            (user) => user.username.toLowerCase() === currentUsernameLower
          );
          finalUsers = matchingUser ? [matchingUser] : [];
        } else {
          if (isUsuarioIserra) {
            shouldShowError = true;
            errorMessage = `Usuario ISERRA "${getCurrentUsername()}" no está autorizado para gestionar cierres de turno.`;
            setUserNotInList(true);
          } else {
            shouldShowError = true;
            errorMessage = `Usuario "${getCurrentUsername()}" no encontrado en la lista de usuarios autorizados.`;
            setUserNotFound(true);
          }
          finalUsers = [];
        }
      }
      if (isMounted.current) {
        setFilteredUsers(finalUsers);
        hasLoadedUsersRef.current = true;
        if (shouldShowError) {
          setErrorUsers(errorMessage);
        }
      }
    } catch (error) {
      if (isMounted.current) {
        const errorMsg = error.message || "No se pudieron cargar los usuarios";
        setErrorUsers(`Error: ${errorMsg}`);
        console.error("Error cargando usuarios:", error);
        if (!isAdmin && currentUser) {
          setUserNotFound(true);
        }
      }
    } finally {
      if (isMounted.current) {
        setLoadingUsers(false);
      }
    }
  }, [isAdmin, isUsuarioIserra, currentUser, getCurrentUsername]);

  useEffect(() => {
    if (!loading && currentUser && !hasLoadedUsersRef.current) {
      loadUsers();
    }
  }, [loading, currentUser, loadUsers]);

  const loadUserTurns = useCallback(async (username) => {
    if (!username || username === "Usuario no identificado") {
      setUserTurns([]);
      setSelectedTurn(null);
      setErrorTurns(null);
      return;
    }
    try {
      setLoadingTurns(true);
      setErrorTurns(null);
      const result = await getUserTurnsService(username);
      if (!isMounted.current) return;
      if (result.success && result.data && result.data.length > 0) {
        setUserTurns(result.data);
        setSelectedTurn(result.data[0]);
        if (formikRef.current) {
          formikRef.current.setFieldValue("selectedTurnId", result.data[0].id.toString());
        }
      } else {
        setUserTurns([]);
        setSelectedTurn(null);
        if (result.error) {
          setErrorTurns(result.error);
        } else {
          setErrorTurns(`No se encontraron turnos para el usuario "${username}"`);
        }
        if (formikRef.current) {
          formikRef.current.setFieldValue("selectedTurnId", "");
        }
      }
    } catch (error) {
      if (isMounted.current) {
        setErrorTurns(error.message || "Error al cargar los turnos");
        setUserTurns([]);
        setSelectedTurn(null);
      }
    } finally {
      if (isMounted.current) {
        setLoadingTurns(false);
      }
    }
  }, []);

  const clearUserTurns = useCallback(() => {
    setUserTurns([]);
    setSelectedTurn(null);
    setErrorTurns(null);
  }, []);

  const getInitialValues = useCallback(() => {
    const baseValues = {
      efectivoInicial:
        initialData?.efectivoInicial !== undefined ? initialData.efectivoInicial : "",
      recaudoEfectivo:
        initialData?.recaudoEfectivo !== undefined ? initialData.recaudoEfectivo : "",
      userId: initialData?.userId || "",
      selectedTurnId: initialData?.selectedTurnId || "",
    };
    if (!isAdmin && filteredUsers.length > 0 && !baseValues.userId && !userNotFound) {
      baseValues.userId = filteredUsers[0].id.toString();
    }
    if (isAdmin && filteredUsers.length > 0 && !baseValues.userId) {
      baseValues.userId = filteredUsers[0].id.toString();
    }
    if (baseValues.efectivoInicial && baseValues.efectivoInicial !== "") {
      const numValue = parseInputToNumber(baseValues.efectivoInicial);
      if (!isNaN(numValue) && numValue >= 0) {
        baseValues.efectivoInicial = formatNumberForDisplay(numValue);
      }
    }
    if (baseValues.recaudoEfectivo && baseValues.recaudoEfectivo !== "") {
      const numValue = parseInputToNumber(baseValues.recaudoEfectivo);
      if (!isNaN(numValue) && numValue >= 0) {
        baseValues.recaudoEfectivo = formatNumberForDisplay(numValue);
      }
    }
    return baseValues;
  }, [isAdmin, initialData, filteredUsers, userNotFound]);

  const validationSchema = Yup.object().shape({
    userId:
      isAdmin && !userNotFound
        ? Yup.string().required("Debe seleccionar un usuario")
        : Yup.string(),
    selectedTurnId:
      isAdmin && !userNotFound ? Yup.string().required("Debe seleccionar un turno") : Yup.string(),
    efectivoInicial: Yup.mixed()
      .test("is-valid-number", "Debe ingresar un número válido (puede ser 0)", (value) => {
        if (value === null || value === undefined) return false;
        if (value === "") return false;
        const numValue = parseInputToNumber(value);
        return !isNaN(numValue) && numValue >= 0;
      })
      .required("El efectivo inicial es obligatorio"),
    recaudoEfectivo: Yup.mixed()
      .test("is-valid-number", "Debe ingresar un número válido (puede ser 0)", (value) => {
        if (value === null || value === undefined) return false;
        if (value === "") return false;
        const numValue = parseInputToNumber(value);
        return !isNaN(numValue) && numValue >= 0;
      })
      .required("El recaudo en efectivo es obligatorio"),
  });

  const prepareFormData = useCallback(
    (values, selectedUser, result, fechaCierreFormatted, selectedTurnData = null) => {
      const efectivoInicialNum = parseInputToNumber(values.efectivoInicial);
      const recaudoEfectivoNum = parseInputToNumber(values.recaudoEfectivo);
      const backendData = result?.data || {};
      const balanceData = backendData.balance?.[0] || {};
      const idSave = backendData.idSave || result?.idSave || null;
      let roleName = "Operador";
      if (isAdmin) {
        roleName = "Administrador";
      } else if (isUsuarioIserra) {
        roleName = "Usuario ISERRA";
      }
      return {
        ...values,
        efectivoInicial: efectivoInicialNum,
        recaudoEfectivo: recaudoEfectivoNum,
        totalRecaudo: recaudoEfectivoNum,
        diferenciaEfectivo: backendData?.diferenciaEfectivo || 0,
        diferenciaTotal: backendData?.diferenciaTotal || 0,
        turnoId: balanceData?.NumeroTurno || "N/A",
        idSave: idSave,
        fechaCierre: fechaCierreFormatted,
        usuarioNombre: selectedUser.username,
        usuarioId: selectedUser.id,
        rol: roleName,
        currentUsername: getCurrentUsername(),
        backendResponse: backendData,
        efectivoSistema: backendData?.trans?.valorE || 0,
        tarjetaSistema: backendData?.trans?.valorT || 0,
        numeroCaja: balanceData?.NumeroCaja || "04",
        numeroTurno: balanceData?.NumeroTurno || "N/A",
        cierreCompletado: true,
        fechaCierreReal: new Date().toISOString(),
        balanceData: balanceData,
        inicioTurno: balanceData?.FHInicio,
        finTurno: balanceData?.FHFin,
        usuarioSistema: backendData.user_kme || "Explotador",
        selectedTurn: selectedTurnData,
        numeroLineaUsado: selectedTurnData?.numeroLinea || null,
      };
    },
    [isAdmin, isUsuarioIserra, getCurrentUsername]
  );

  const generateAndSendPDF = async (formDataComplete, result) => {
    try {
      setAutoProcessing(true);
      setAutoProcessingError(null);

      const pdfData = {
        formData: formDataComplete,
        responseData: result,
        isAdmin: isAdmin,
        isExplotador: false,
        isOperador: isOperador,
      };

      const pdfResult = await generarPDFCierreTurno(pdfData);

      if (pdfResult && pdfResult.pdfUrl) {
        setPdfGeneratedData({
          pdfUrl: pdfResult.pdfUrl,
          savedOnServer: pdfResult.savedOnServer || false,
          serverMessage: pdfResult.serverMessage || "",
          fileName: `cierre_turno_${formDataComplete.turnoId || "turno"}.pdf`,
        });

        setTimeout(() => {
          const link = document.createElement("a");
          link.href = pdfResult.pdfUrl;
          link.download = `cierre_turno_${formDataComplete.turnoId || "turno"}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }, 500);

        return true;
      } else {
        throw new Error("No se pudo generar el PDF");
      }
    } catch (error) {
      console.error("❌ Error generando PDF automático:", error);
      setAutoProcessingError(error.message || "Error al generar el PDF automático");
      return false;
    } finally {
      setAutoProcessing(false);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitError(null);
      setBackendCierreResult(null);
      setDebugInfo(null);
      setAutoProcessingError(null);
      setPdfGeneratedData(null);
      setSubmitting(true);

      if (!currentUser) {
        throw new Error("Usuario no autenticado.");
      }
      if (userNotInList || userNotFound) {
        throw new Error(
          userNotInList
            ? "Usuario no autorizado para gestionar cierres de turno"
            : "Usuario no encontrado en la lista de usuarios autorizados"
        );
      }

      let selectedUser;
      let usernameExacto;
      if (isAdmin && values.userId) {
        selectedUser = filteredUsers.find((u) => u.id === Number(values.userId));
        usernameExacto =
          selectedUser?.rawUser?.Usuario ||
          selectedUser?.rawUser?.usuario ||
          selectedUser?.username;
      } else if (filteredUsers.length > 0) {
        selectedUser = filteredUsers[0];
        usernameExacto =
          selectedUser?.rawUser?.Usuario ||
          selectedUser?.rawUser?.usuario ||
          selectedUser?.username;
      } else {
        selectedUser = currentUser;
        usernameExacto =
          currentUser?.rawData?.user?.username || currentUser?.username || getCurrentUsername();
      }

      if (!usernameExacto || usernameExacto === "Usuario no identificado") {
        throw new Error("No se pudo determinar el usuario para el cierre.");
      }

      let selectedTurnData = null;
      if (isAdmin && values.selectedTurnId) {
        selectedTurnData = userTurns.find((turn) => turn.id === Number(values.selectedTurnId));
        if (!selectedTurnData) {
          throw new Error("Turno seleccionado no válido");
        }
      }

      const now = new Date();
      const fechaCierreFormatted = formatDateForAPI(now);
      const efectivoInicialNum = parseInputToNumber(values.efectivoInicial);
      const recaudoEfectivoNum = parseInputToNumber(values.recaudoEfectivo);

      // Construir cierreData base (sin numeroLinea por defecto)
      const cierreData = {
        user_kme: usernameExacto,
        user_gp: usernameExacto,
        efectivoInicial: efectivoInicialNum || 0,
        recaudoEfectivo: recaudoEfectivoNum || 0,
        fechaCierre: fechaCierreFormatted,
      };

      // Solo agregar numeroLinea si es administrador Y hay turno seleccionado
      if (isAdmin && selectedTurnData && selectedTurnData.numeroLinea) {
        cierreData.numeroLinea = selectedTurnData.numeroLinea;
      } else if (!isAdmin) {
      }

      const result = await closeTurnoService(cierreData);

      if (result.hasBalance && result.success === false) {
        const modifiedResult = { ...result, success: true, error: null, errorType: null };
        setBackendCierreResult(modifiedResult);
      } else if (!result.success) {
        let errorMsg = result.error || "Error al cerrar el turno en el servidor";
        if (
          result.errorType === "TURNO_NO_ENCONTRADO" ||
          errorMsg.includes("TURNO_NO_ENCONTRADO") ||
          errorMsg.includes("No se encontró") ||
          errorMsg.includes("turno activo")
        ) {
          throw new Error(`TURNO_NO_ENCONTRADO: ${errorMsg}`);
        }
        throw new Error(errorMsg);
      } else {
        setBackendCierreResult(result);
      }

      const formDataComplete = prepareFormData(
        values,
        selectedUser,
        result,
        fechaCierreFormatted,
        selectedTurnData
      );

      const pdfGenerated = await generateAndSendPDF(formDataComplete, result);

      if (!pdfGenerated) {
        console.warn("⚠️ El PDF no se pudo generar automáticamente, pero el turno se cerró");
      }

      if (typeof onSubmit === "function") {
        onSubmit(formDataComplete);
      }

      setSubmitSuccess(true);

      return {
        success: true,
        data: formDataComplete,
        backendResult: result,
        shouldShowDialog: true,
        idSave: result.idSave,
        pdfGenerated: pdfGenerated,
        pdfData: pdfGeneratedData,
        autoProcessingError: autoProcessingError,
      };
    } catch (error) {
      let errorMessage = error.message || "Error al cerrar el turno";
      if (errorMessage.includes("TURNO_NO_ENCONTRADO")) {
        errorMessage = `⚠️ No se encontró un turno activo para el usuario`;
      }
      console.error("❌ Error en handleSubmit:", errorMessage);
      setSubmitError(errorMessage);
      return {
        success: false,
        error: errorMessage,
        shouldShowDialog: false,
        debugInfo,
      };
    } finally {
      setSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema,
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  const formikRef = useRef(formik);
  useEffect(() => {
    formikRef.current = formik;
  }, [formik]);

  useEffect(() => {
    if (isAdmin && formik.values.userId && filteredUsers.length > 0) {
      const selectedUser = filteredUsers.find((u) => u.id === Number(formik.values.userId));
      if (selectedUser && selectedUser.username) {
        loadUserTurns(selectedUser.username);
      } else {
        clearUserTurns();
      }
    } else if (!isAdmin) {
      clearUserTurns();
    }
  }, [isAdmin, formik.values.userId, filteredUsers, loadUserTurns, clearUserTurns]);

  const handleInputChangeWithFormat = useCallback(
    (fieldName) => (event) => {
      let value = event.target.value;
      value = value.replace(/[^0-9.,]/g, "");
      formik.setFieldValue(fieldName, value);
    },
    [formik]
  );

  const handleInputBlurWithFormat = useCallback(
    (fieldName) => (event) => {
      let value = event.target.value;
      if (value && value !== "") {
        const numValue = parseInputToNumber(value);
        if (!isNaN(numValue) && numValue >= 0) {
          const formattedValue = formatNumberForDisplay(numValue);
          formik.setFieldValue(fieldName, formattedValue);
        }
      } else if (value === "") {
        formik.setFieldValue(fieldName, "");
      }
      formik.handleBlur(event);
    },
    [formik]
  );

  const getNumericValue = useCallback(
    (fieldName) => {
      const value = formik.values[fieldName];
      if (value === null || value === undefined) return 0;
      if (value === "") return 0;
      return parseInputToNumber(value);
    },
    [formik.values]
  );

  const getFormattedValue = useCallback((fieldName) => formik.values[fieldName], [formik.values]);

  const getSelectedUserName = useCallback(() => {
    if (formik.values.userId && filteredUsers.length > 0) {
      const user = filteredUsers.find((u) => u.id === Number(formik.values.userId));
      return user?.username || getCurrentUsername();
    }
    return getCurrentUsername();
  }, [formik.values.userId, filteredUsers, getCurrentUsername]);

  const resetFormState = useCallback(() => {
    if (isMounted.current) {
      formik.resetForm();
      setSubmitError(null);
      setSubmitSuccess(false);
      setBackendCierreResult(null);
      setDebugInfo(null);
      setUserTurns([]);
      setSelectedTurn(null);
      setErrorTurns(null);
      setAutoProcessing(false);
      setAutoProcessingError(null);
      setPdfGeneratedData(null);
    }
  }, [formik]);

  return {
    values: formik.values,
    errors: formik.errors,
    touched: formik.touched,
    isSubmitting: formik.isSubmitting,
    handleChange: formik.handleChange,
    handleBlur: formik.handleBlur,
    handleSubmit: formik.handleSubmit,
    setFieldValue: formik.setFieldValue,
    handleInputChangeWithFormat,
    handleInputBlurWithFormat,
    getFormattedValue,
    getNumericValue,
    parseInputToNumber,
    formatNumberForDisplay,
    resetForm: resetFormState,
    submitError,
    submitSuccess,
    setSubmitError,
    setSubmitSuccess,
    isAdmin,
    isUsuarioIserra,
    isOperador,
    currentUser,
    userRole,
    getCurrentUsername,
    loading,
    filteredUsers,
    loadingUsers,
    errorUsers,
    userNotInList,
    userNotFound,
    loadUsers,
    getSelectedUserName,
    backendCierreResult,
    setBackendCierreResult,
    debugInfo,
    userTurns,
    loadingTurns,
    selectedTurn,
    setSelectedTurn,
    errorTurns,
    loadUserTurns,
    clearUserTurns,
    autoProcessing,
    autoProcessingError,
    pdfGeneratedData,
  };
};
